import React, { useMemo } from 'react';
import { FiCalendar } from 'react-icons/fi';

const BillingFrequencyChart = ({ products, loading }) => {
    const billingData = useMemo(() => {
        const billingCounts = products?.reduce((acc, product) => {
            acc[product.billable] = (acc[product.billable] || 0) + 1;
            return acc;
        }, {}) || {};

        const totalCount = Object.values(billingCounts).reduce((sum, count) => sum + count, 0) || 1;
        
        return { billingCounts, totalCount };
    }, [products]);

    // Define colors for each billing frequency
    const billingColors = {
        "monthly": "#3B82F6",
        "yearly": "#10B981",
        "quarterly": "#8B5CF6",
        "semi-annually": "#F59E0B"
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-5 h-full">
                <div className="animate-pulse">
                    <div className="h-5 bg-gray-200 rounded w-1/3 mb-6"></div>
                    <div className="h-20 bg-gray-200 rounded w-full mb-4"></div>
                    <div className="grid grid-cols-2 gap-2">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="flex items-center space-x-2">
                                <div className="h-3 w-3 bg-gray-200 rounded-full"></div>
                                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm p-5 h-full">
            <div className="flex justify-between items-center mb-5">
                <h3 className="text-lg font-semibold text-gray-800">Billing Cycles</h3>
                <div className="p-2 rounded-lg bg-green-50">
                    <FiCalendar className="text-green-500" />
                </div>
            </div>
            
            <div className="relative">
                <div className="flex h-20 rounded-lg overflow-hidden mb-4 shadow-sm">
                    {Object.entries(billingData.billingCounts).map(([period, count]) => (
                        <div 
                            key={period} 
                            className="hover:opacity-80 transition-all duration-300 flex items-center justify-center"
                            style={{ 
                                width: `${(count / billingData.totalCount) * 100}%`,
                                backgroundColor: billingColors[period] || '#CBD5E1' 
                            }}
                            title={`${period}: ${count} plans (${Math.round((count / billingData.totalCount) * 100)}%)`}
                        >
                            {(count / billingData.totalCount) > 0.15 && (
                                <span className="text-white font-bold text-sm">
                                    {Math.round((count / billingData.totalCount) * 100)}%
                                </span>
                            )}
                        </div>
                    ))}
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                    {Object.entries(billingData.billingCounts).map(([period, count]) => (
                        <div key={period} className="flex items-center text-sm group">
                            <div 
                                className="w-3 h-3 rounded-full mr-2 transition-transform duration-300 group-hover:scale-125"
                                style={{ backgroundColor: billingColors[period] || '#CBD5E1' }}
                            ></div>
                            <span className="capitalize text-gray-700">{period}</span>
                            <span className="ml-auto font-semibold">{count}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BillingFrequencyChart; 